import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, ArrowLeft, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

const TYPES = [
  { v: "sponsor", l: "Sponsor a season / Saturday Challenge" },
  { v: "brand_ad", l: "Run a brand ad campaign (House Ads)" },
  { v: "media", l: "Media / press partnership" },
  { v: "integration", l: "Integration / API partnership" },
  { v: "other", l: "Something else" },
];
const BUDGETS = [
  { v: "tbd", l: "To be discussed" },
  { v: "under_1k", l: "Under $1,000" },
  { v: "1k_5k", l: "$1,000 – $5,000" },
  { v: "5k_25k", l: "$5,000 – $25,000" },
  { v: "25k_plus", l: "$25,000+" },
];

export default function Partner() {
  const [f, setF] = useState({
    name: "", email: "", company: "", role: "", country: "",
    partnership_type: "sponsor", budget_band: "tbd", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (f.message.length < 10) { toast.error("Tell us a bit more (min 10 chars)"); return; }
    setBusy(true);
    try {
      await api.post("/partners/inquire", f);
      setDone(true);
      toast.success("Thanks — we'll be in touch within 48 hours.");
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="theme-arena min-h-screen">
      <header className="border-b" style={{ borderColor: "rgba(11,61,46,0.18)" }}>
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm" style={{ color: "#0B3D2E", borderBottom: "none" }}>
            <ArrowLeft size={14} weight="bold" /> NKognito
          </Link>
          <span className="text-xs uppercase tracking-widest" style={{ color: "#0B3D2E", opacity: 0.7 }}>For brands & partners</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-20">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm mb-5"
               style={{ background: "rgba(212, 175, 55, 0.15)", border: "1px solid rgba(212, 175, 55, 0.4)" }}>
            <Handshake size={22} weight="duotone" style={{ color: "#D4AF37" }} />
          </div>
          <p className="eyebrow mb-2">Partnerships</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">Build your brand inside Africa's smartest game.</h1>
          <p className="text-base mt-4 max-w-2xl leading-relaxed" style={{ color: "#101518", opacity: 0.85 }}>
            NKognito reaches Africa's most engaged trivia players. We work with sponsors on Saturday Challenges, season prize pools, branded question slates, and in-app ads. Tell us what you're thinking — we reply within 48 hours.
          </p>
        </div>

        {done ? (
          <div className="arena-card p-8 text-center border-l-2 rounded-sm"
               style={{ background: "#FFFFFF", borderLeftColor: "#0B3D2E" }} data-testid="partner-thanks">
            <CheckCircle size={32} weight="duotone" style={{ color: "#1F7A3F" }} className="mx-auto mb-3" />
            <h2 className="font-display text-2xl mb-2">Inquiry received</h2>
            <p className="text-sm" style={{ color: "#101518", opacity: 0.75 }}>
              Our partnership team will reach out to <span className="font-mono" style={{ color: "#0B3D2E" }}>{f.email}</span> within 48 hours.
            </p>
            <Link to="/" className="arena-link mt-6 inline-block text-xs">← Back to NKognito</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="arena-card p-6 sm:p-8 space-y-4 rounded-sm"
                style={{ background: "#FFFFFF", border: "1px solid rgba(11,61,46,0.18)" }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <F label="Your name *"><I v={f.name} onC={(v) => set("name", v)} required minLength={2} maxLength={80} testId="partner-name" /></F>
              <F label="Email *"><I type="email" v={f.email} onC={(v) => set("email", v)} required testId="partner-email" /></F>
              <F label="Company"><I v={f.company} onC={(v) => set("company", v)} maxLength={120} testId="partner-company" /></F>
              <F label="Your role"><I v={f.role} onC={(v) => set("role", v)} maxLength={80} /></F>
              <F label="Country"><I v={f.country} onC={(v) => set("country", v)} maxLength={40} placeholder="Nigeria / Ghana / Global" /></F>
              <F label="Partnership type *">
                <select value={f.partnership_type} onChange={(e) => set("partnership_type", e.target.value)}
                        data-testid="partner-type"
                        className="w-full px-3 py-2.5 text-sm rounded-sm outline-none"
                        style={{ background: "#F8F1E3", border: "1px solid rgba(11,61,46,0.25)", color: "#101518" }}>
                  {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </F>
            </div>
            <F label="Budget band">
              <select value={f.budget_band} onChange={(e) => set("budget_band", e.target.value)}
                      className="w-full px-3 py-2.5 text-sm rounded-sm outline-none"
                      style={{ background: "#F8F1E3", border: "1px solid rgba(11,61,46,0.25)", color: "#101518" }}>
                {BUDGETS.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
              </select>
            </F>
            <F label="Tell us about it *">
              <textarea value={f.message} onChange={(e) => set("message", e.target.value)} rows={5} maxLength={2000}
                        required minLength={10}
                        data-testid="partner-message"
                        placeholder="What's your goal, what scale are you thinking, and when do you want to launch?"
                        className="w-full px-3 py-2.5 text-sm rounded-sm outline-none"
                        style={{ background: "#F8F1E3", border: "1px solid rgba(11,61,46,0.25)", color: "#101518" }} />
              <p className="text-[10px] mt-1 text-right" style={{ color: "#101518", opacity: 0.5 }}>{f.message.length}/2000</p>
            </F>
            <button type="submit" disabled={busy} data-testid="partner-submit"
                    className="arena-button-primary w-full sm:w-auto px-6 py-3 font-medium rounded-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
              <PaperPlaneTilt size={14} weight="bold" />
              {busy ? "Sending…" : "Send partnership inquiry"}
            </button>
            <p className="text-[11px]" style={{ color: "#101518", opacity: 0.55 }}>By submitting you agree we can email you about this inquiry. We won't add you to any marketing lists.</p>
          </form>
        )}
      </main>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider" style={{ color: "#0B3D2E", opacity: 0.75 }}>{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function I({ v, onC, type = "text", maxLength, minLength, required, placeholder, testId }) {
  return (
    <input type={type} value={v} onChange={(e) => onC(e.target.value)}
           maxLength={maxLength} minLength={minLength} required={required} placeholder={placeholder}
           data-testid={testId}
           className="w-full px-3 py-2.5 text-sm rounded-sm outline-none"
           style={{ background: "#F8F1E3", border: "1px solid rgba(11,61,46,0.25)", color: "#101518" }} />
  );
}
